// Pure logic for the address suggestions under the An/Cc line in the compose
// dialog (#820, UI to the #777 route). Node-testable, no React.
import type { MailAddressSuggestion } from '@embertime/shared'

/** Same threshold as the API (`SUGGESTION_MIN_QUERY` in mail-suggestions.ts). */
export const SUGGESTION_MIN_QUERY = 2
/** The API turns a longer `q` into '' anyway (schema max 200). */
export const SUGGESTION_MAX_QUERY = 200
export const SUGGESTION_LIMIT = 8

/** Start offsets of all address segments. A `,`/`;` inside double quotes does
 * not separate (same rule as AddressPills); `\"` inside quotes is escaped. */
function segmentStarts(value: string): number[] {
  const starts = [0]
  let quoted = false
  for (let i = 0; i < value.length; i++) {
    const ch = value[i]
    if (quoted && ch === '\\') i++
    else if (ch === '"') quoted = !quoted
    else if (!quoted && (ch === ',' || ch === ';')) starts.push(i + 1)
  }
  return starts
}

/** Start index of the segment being typed and its trimmed text. */
export function lastAddressSegment(value: string): { start: number; query: string } {
  const start = segmentStarts(value).at(-1) ?? 0
  return { start, query: value.slice(start).trim() }
}

/** RFC 5322 mailbox: bare address without a name, quoted display name when it
 * contains specials. */
export function formatMailbox(s: Pick<MailAddressSuggestion, 'address' | 'name'>): string {
  const name = s.name?.trim()
  if (!name) return s.address
  if (!/[,;"<>()[\]:@\\]/.test(name)) return `${name} <${s.address}>`
  return `"${name.replace(/(["\\])/g, '\\$1')}" <${s.address}>`
}

/** Replaces only the typed segment with the mailbox and appends ", ". */
export function applySuggestion(
  value: string,
  s: Pick<MailAddressSuggestion, 'address' | 'name'>,
): string {
  const prefix = value.slice(0, lastAddressSegment(value).start).trimEnd()
  return `${prefix ? `${prefix} ` : ''}${formatMailbox(s)}, `
}

export type HighlightSegment = { text: string; match: boolean }

/** First case-insensitive occurrence of `query` as its own segment. */
export function highlightSegments(text: string, query: string): HighlightSegment[] {
  const q = query.trim().toLowerCase()
  const at = q ? text.toLowerCase().indexOf(q) : -1
  if (at < 0) return [{ text, match: false }]
  return [
    { text: text.slice(0, at), match: false },
    { text: text.slice(at, at + q.length), match: true },
    { text: text.slice(at + q.length), match: false },
  ].filter((s) => s.text)
}

export type SuggestionListState = { open: boolean; active: number }

export const CLOSED_SUGGESTIONS: SuggestionListState = { open: false, active: 0 }

/** Keyboard handling for an open list. `handled` means: preventDefault and
 * stopPropagation (Esc must not also close the SidePanel). Tab closes but
 * keeps the browser default, so focus still moves on. */
export function suggestionKey(
  state: SuggestionListState,
  key: string,
  count: number,
): { state: SuggestionListState; pick?: number; handled: boolean } {
  if (!state.open || count === 0) return { state, pick: undefined, handled: false }
  switch (key) {
    case 'ArrowDown':
      return { state: { open: true, active: (state.active + 1) % count }, handled: true }
    case 'ArrowUp':
      return { state: { open: true, active: (state.active - 1 + count) % count }, handled: true }
    case 'Enter':
      return { state: CLOSED_SUGGESTIONS, pick: Math.min(state.active, count - 1), handled: true }
    case 'Escape':
      return { state: CLOSED_SUGGESTIONS, pick: undefined, handled: true }
    case 'Tab':
      return { state: CLOSED_SUGGESTIONS, pick: undefined, handled: false }
    default:
      return { state, pick: undefined, handled: false }
  }
}

/** Addresses already present in the field (lowercase), without the segment
 * being typed. */
function presentAddresses(value: string): Set<string> {
  const starts = segmentStarts(value)
  const present = new Set<string>()
  for (let i = 0; i < starts.length - 1; i++) {
    const part = value.slice(starts[i], starts[i + 1] - 1)
    const angle = /<([^<>]+)>\s*$/.exec(part)
    const address = (angle ? angle[1] : part).trim().toLowerCase()
    if (address) present.add(address)
  }
  return present
}

export function filterExistingSuggestions<T extends Pick<MailAddressSuggestion, 'address'>>(
  suggestions: T[],
  value: string,
): T[] {
  const present = presentAddresses(value)
  return suggestions.filter((s) => !present.has(s.address.toLowerCase()))
}

const MONTHS = [
  'Jan.',
  'Feb.',
  'März',
  'Apr.',
  'Mai',
  'Juni',
  'Juli',
  'Aug.',
  'Sept.',
  'Okt.',
  'Nov.',
  'Dez.',
]

/** "zuletzt benutzt" in the list: heute, gestern, vor N Tg., vor N Wo., then
 * the month (with year outside the current year). Calendar days, local time. */
export function formatLastUsed(iso: string, now: Date = new Date()): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  const days = Math.round((day(now) - day(at)) / 86_400_000)
  if (days <= 0) return 'heute'
  if (days === 1) return 'gestern'
  if (days < 14) return `vor ${days} Tg.`
  if (days < 35) return `vor ${Math.floor(days / 7)} Wo.`
  const month = MONTHS[at.getMonth()]
  return at.getFullYear() === now.getFullYear() ? month : `${month} ${at.getFullYear()}`
}

/** Up to two initials from the name, "?" when only the address is known. */
export function suggestionInitials(s: { name: string | null }): string {
  const words = (s.name ?? '').trim().split(/\s+/).filter(Boolean)
  if (!words.length) return '?'
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')
}

/** Client-side gate (#524): never ask for a protected or unknown account, and
 * only from the same minimum length as the server. The server refuses as well. */
export function shouldSuggest(account: { protected: boolean } | undefined, query: string): boolean {
  if (!account || account.protected) return false
  const q = query.trim()
  return q.length >= SUGGESTION_MIN_QUERY && q.length <= SUGGESTION_MAX_QUERY
}
